import { useCallback, useEffect, useState } from 'react';

import {
  getStandaloneChargePublicKyc,
  StandaloneChargePublicKycDTO,
} from '../../../services/standalone-charges';

const AUTO_REFRESH_MS = 60_000;

export const useStandaloneKyc = (token?: string) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StandaloneChargePublicKycDTO | null>(null);

  const fetchKyc = useCallback(
    async (silent = false) => {
      if (!token) {
        if (!silent) setLoading(false);
        setError('Link de biometria inválido.');
        return;
      }

      if (!silent) setLoading(true);

      try {
        const result = await getStandaloneChargePublicKyc(token);
        setData(result);
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Não foi possível carregar os dados de biometria.'
        );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Link de biometria inválido.');
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const result = await getStandaloneChargePublicKyc(token);
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Não foi possível carregar os dados de biometria.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token || data?.status === 'COMPLETED') return;

    const id = window.setInterval(() => fetchKyc(true), AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [token, data?.status, fetchKyc]);

  return { loading, error, data, refetch: () => fetchKyc(true) };
};
